import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import MainLayout from '../../components/layout/MainLayout';
import Avatar from '../../components/common/Avatar';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Switch } from '../../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  User,
  Mail,
  Phone,
  MapPin,
  Lock,
  Save,
  Loader2,
  Camera,
  Shield,
  Bell,
  CreditCard,
  Building2,
  Calendar,
  CheckCircle2,
  Eye,
  EyeOff,
  Landmark,
  FileText,
  AlertCircle,
  MessageSquare,
  CalendarCheck,
  Receipt,
  Gift,
  Star,
  TrendingUp,
  Megaphone,
  Lightbulb,
  Euro
} from 'lucide-react';
import { cn } from '../../lib/utils';
import api from '../../lib/axios';
import { toast } from 'sonner';

const ProfilePage = () => {
  const { user, refreshUser } = useAuth();
  const { theme } = useTheme();
  const [loading, setLoading] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const [savingNotifications, setSavingNotifications] = useState(false);
  
  // Check if user is a team member (shows extra fields - company, bank, etc.)
  const isTeamMember = user?.role === 'team' || 
    user?.role === 'team_member' || 
    user?.role === 'project_manager' ||
    user?.role === 'subcontractor' ||
    user?.role === 'affiliate' ||
    user?.role === 'creative' ||
    user?.role === 'customer_service' ||
    user?.team_member_type; // Also check team_member_type field
  const isClient = user?.role === 'client';
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const fileInputRef = React.useRef(null);

  const [profile, setProfile] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    company: '',
    address: '',
    postal_code: '',
    city: '',
    bio: '',
    // Business info for members
    siret: '',
    vat_number: '',
    tva_number: '',
    business_name: '',
    business_address: '',
    company_name: '',
    company_address: '',
    company_postal_code: '',
    company_city: '',
    legal_status: '',
    // Bank details for members
    iban: '',
    bic: '',
    bank_name: '',
    account_holder: '',
    // Rates for members
    hourly_rate: 0,
    daily_rate: 0
  });

  const [passwords, setPasswords] = useState({
    current: '',
    new: '',
    confirm: ''
  });

  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });

  // Préférences de notification complètes
  const [notifPrefs, setNotifPrefs] = useState({
    // Canaux
    email_enabled: true,
    push_enabled: true,
    // Types de notifications
    new_message: true,
    project_updates: true,
    appointment_reminder: true,
    appointment_validation: true,
    new_invoice: true,
    budget_estimate_validation: true,
    gift_code_received: true,
    project_evaluation_request: true,
    google_review_request: true,
    project_budget_summary: true,
    // Préférences commerciales
    commercial_offers: false,
    commercial_offers_channel: 'email',
    agency_tips: true,
    agency_tips_channel: 'both'
  });

  const handleAvatarClick = () => {
    fileInputRef.current?.click();
  };

  const handleAvatarChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Veuillez sélectionner une image');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('L\'image ne doit pas dépasser 5 Mo');
      return;
    }

    setUploadingAvatar(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      await api.post('/users/me/avatar', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      toast.success('Photo de profil mise à jour !');
      if (refreshUser) {
        await refreshUser();
      }
    } catch (error) {
      console.error('Error uploading avatar:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors du téléchargement');
    } finally {
      setUploadingAvatar(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  useEffect(() => {
    if (user) {
      setProfile({
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        email: user.email || '',
        phone: user.phone || '',
        company: user.company || '',
        address: user.address || '',
        postal_code: user.postal_code || '',
        city: user.city || '',
        bio: user.bio || '',
        // Business info - support both naming conventions
        siret: user.siret || '',
        vat_number: user.vat_number || user.tva_number || '',
        tva_number: user.tva_number || user.vat_number || '',
        business_name: user.business_name || user.company_name || '',
        business_address: user.business_address || user.company_address || '',
        company_name: user.company_name || user.business_name || '',
        company_address: user.company_address || user.business_address || '',
        company_postal_code: user.company_postal_code || '',
        company_city: user.company_city || '',
        legal_status: user.legal_status || '',
        // Bank details
        iban: user.iban || '',
        bic: user.bic || '',
        bank_name: user.bank_name || '',
        account_holder: user.account_holder || `${user.first_name || ''} ${user.last_name || ''}`.trim()
      });
    }
    
    // Charger les préférences de notification
    fetchNotificationPreferences();
  }, [user]);

  const fetchNotificationPreferences = async () => {
    try {
      const response = await api.get('/users/me/notification-preferences');
      setNotifPrefs(response.data);
    } catch (error) {
      console.error('Error fetching notification preferences:', error);
    }
  };

  const handleSaveProfile = async () => {
    setSavingProfile(true);
    try {
      await api.put('/users/me', profile);
      toast.success('Profil mis à jour !');
      if (refreshUser) refreshUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour');
    } finally {
      setSavingProfile(false);
    }
  };

  const handleChangePassword = async () => {
    if (passwords.new !== passwords.confirm) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }
    if (passwords.new.length < 6) {
      toast.error('Le mot de passe doit faire au moins 6 caractères');
      return;
    }

    setSavingPassword(true);
    try {
      await api.put('/users/me/password', {
        current_password: passwords.current,
        new_password: passwords.new
      });
      toast.success('Mot de passe modifié !');
      setPasswords({ current: '', new: '', confirm: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du changement de mot de passe');
    } finally {
      setSavingPassword(false);
    }
  };

  const handleSaveNotifications = async () => {
    setSavingNotifications(true);
    try {
      await api.put('/users/me/notification-preferences', notifPrefs);
      toast.success('Préférences de notification mises à jour !');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour');
    } finally {
      setSavingNotifications(false);
    }
  };

  const updateNotifPref = (key, value) => {
    setNotifPrefs(prev => ({ ...prev, [key]: value }));
  };

  const getRoleBadge = (role) => {
    const styles = {
      agency: 'bg-purple-100 text-purple-700',
      client: 'bg-blue-100 text-blue-700',
      team_member: 'bg-green-100 text-green-700'
    };
    const labels = { agency: 'Agence', client: 'Client', team_member: 'Équipe' };
    return <Badge className={styles[role] || 'bg-gray-100'}>{labels[role] || role}</Badge>;
  };

  // Build full avatar URL
  const getAvatarUrl = (avatarPath) => {
    if (!avatarPath) return null;
    if (avatarPath.startsWith('http')) return avatarPath;
    // For relative paths, prepend the backend URL
    const backendUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
    return `${backendUrl}${avatarPath}`;
  };

  // Configuration des notifications par catégorie - différentes selon le rôle
  const clientNotificationCategories = [
    {
      title: "Communications",
      icon: MessageSquare,
      items: [
        { key: 'new_message', label: 'Nouveaux messages', desc: 'Recevoir les notifications de nouveaux messages' },
        { key: 'project_updates', label: 'Mises à jour projets', desc: 'Être notifié des changements sur vos projets' },
      ]
    },
    {
      title: "Rendez-vous",
      icon: CalendarCheck,
      items: [
        { key: 'appointment_reminder', label: 'Rappels de RDV', desc: 'Recevoir des rappels avant vos rendez-vous' },
        { key: 'appointment_validation', label: 'RDV à valider', desc: 'Être notifié des demandes de RDV à valider' },
      ]
    },
    {
      title: "Finances & Budget",
      icon: Receipt,
      items: [
        { key: 'new_invoice', label: 'Nouvelles factures', desc: 'Être notifié à la réception d\'une facture' },
        { key: 'budget_estimate_validation', label: 'Estimations à valider', desc: 'Recevoir les estimations de budget à valider' },
        { key: 'gift_code_received', label: 'Codes cadeaux', desc: 'Être notifié lors de l\'ajout d\'un code cadeau' },
        { key: 'project_budget_summary', label: 'Résumé budget projet', desc: 'Recevoir un résumé si budget économisé sur un projet' },
      ]
    },
    {
      title: "Évaluations & Avis",
      icon: Star,
      items: [
        { key: 'project_evaluation_request', label: 'Demandes d\'évaluation', desc: 'Être notifié pour évaluer un projet terminé' },
        { key: 'google_review_request', label: 'Demande d\'avis Google', desc: 'Recevoir les demandes d\'avis Google avec lien' },
      ]
    }
  ];

  const memberNotificationCategories = [
    {
      title: "Communications",
      icon: MessageSquare,
      items: [
        { key: 'new_message', label: 'Nouveaux messages', desc: 'Messages dans le chat équipe' },
        { key: 'team_mentions', label: 'Mentions', desc: 'Quand vous êtes mentionné dans une conversation' },
      ]
    },
    {
      title: "Projets & Tâches",
      icon: TrendingUp,
      items: [
        { key: 'task_assigned', label: 'Nouvelles tâches', desc: 'Être notifié des nouvelles tâches assignées' },
        { key: 'project_assigned', label: 'Projets assignés', desc: 'Être notifié des nouveaux projets' },
        { key: 'task_deadline', label: 'Rappels échéances', desc: 'Recevoir des rappels avant les deadlines' },
      ]
    },
    {
      title: "Planning & RDV",
      icon: CalendarCheck,
      items: [
        { key: 'appointment_reminder', label: 'Rappels de RDV', desc: 'Recevoir des rappels avant vos rendez-vous' },
        { key: 'schedule_change', label: 'Changements planning', desc: 'Être notifié des modifications de planning' },
      ]
    },
    {
      title: "Rapports & Rémunération",
      icon: Receipt,
      items: [
        { key: 'report_generated', label: 'Nouveau rapport', desc: 'Être notifié quand un rapport est généré' },
        { key: 'report_validated', label: 'Rapport validé', desc: 'Être notifié quand votre rapport est validé' },
        { key: 'payment_sent', label: 'Paiement envoyé', desc: 'Être notifié des virements effectués' },
        { key: 'bonus_awarded', label: 'Prime attribuée', desc: 'Être notifié des primes accordées' },
      ]
    },
    {
      title: "Contrats",
      icon: FileText,
      items: [
        { key: 'contract_to_sign', label: 'Contrat à signer', desc: 'Être notifié des nouveaux contrats' },
        { key: 'contract_renewal', label: 'Renouvellement', desc: 'Rappel avant expiration de contrat' },
      ]
    }
  ];

  // Sélectionner les catégories selon le rôle
  const notificationCategories = isClient ? clientNotificationCategories : memberNotificationCategories;

  return (
    <MainLayout>
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-6 mb-8">
          <div className="relative">
            {user?.avatar_url ? (
              <img 
                src={getAvatarUrl(user.avatar_url)} 
                alt="Avatar" 
                className="w-20 h-20 rounded-full object-cover border-4 border-white shadow-lg"
              />
            ) : (
              <Avatar name={`${user?.first_name} ${user?.last_name}`} size="xl" type={user?.role === 'agency' ? 'agency' : user?.role === 'client' ? 'client' : 'creative'} />
            )}
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleAvatarChange}
              accept="image/*"
              className="hidden"
            />
            <button 
              onClick={handleAvatarClick}
              disabled={uploadingAvatar}
              className="absolute bottom-0 right-0 w-8 h-8 rounded-full bg-white border border-[#e2e8f0] flex items-center justify-center shadow-sm hover:bg-[#f8fafc] disabled:opacity-50"
            >
              {uploadingAvatar ? (
                <Loader2 className="w-4 h-4 text-[#64748b] animate-spin" />
              ) : (
                <Camera className="w-4 h-4 text-[#64748b]" />
              )}
            </button>
          </div>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold" style={{ color: theme.colors?.primary }}>
                {user?.first_name} {user?.last_name}
              </h1>
              {getRoleBadge(user?.role)}
            </div>
            <p className="text-[#64748b]">{user?.email}</p>
            <p className="text-sm text-[#94a3b8] flex items-center gap-1 mt-1">
              <Calendar className="w-3 h-3" />
              Membre depuis {new Date(user?.created_at || Date.now()).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })}
            </p>
          </div>
        </div>

          <Tabs defaultValue="profile" className="space-y-6">
            <TabsList>
              <TabsTrigger value="profile" className="flex items-center gap-2">
                <User className="w-4 h-4" />Profil
              </TabsTrigger>
              <TabsTrigger value="security" className="flex items-center gap-2">
                <Shield className="w-4 h-4" />Sécurité
              </TabsTrigger>
              <TabsTrigger value="notifications" className="flex items-center gap-2">
                <Bell className="w-4 h-4" />Notifications
              </TabsTrigger>
              <TabsTrigger value="tutorial" className="flex items-center gap-2">
                <Lightbulb className="w-4 h-4" />Tutoriel
              </TabsTrigger>
            </TabsList>

            {/* Profile Tab */}
            <TabsContent value="profile">
              <Card className="card">
                <CardHeader>
                  <CardTitle>Informations personnelles</CardTitle>
                  <CardDescription>Mettez à jour vos informations de profil</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label>Prénom</Label>
                      <Input value={profile.first_name} onChange={(e) => setProfile({ ...profile, first_name: e.target.value })} className="mt-1" />
                    </div>
                    <div>
                      <Label>Nom</Label>
                      <Input value={profile.last_name} onChange={(e) => setProfile({ ...profile, last_name: e.target.value })} className="mt-1" />
                    </div>
                  </div>

                  <div>
                    <Label>Email</Label>
                    <Input type="email" value={profile.email} onChange={(e) => setProfile({ ...profile, email: e.target.value })} className="mt-1" />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label>Téléphone</Label>
                      <Input value={profile.phone} onChange={(e) => setProfile({ ...profile, phone: e.target.value })} placeholder="+33 6 12 34 56 78" className="mt-1" />
                    </div>
                    <div>
                      <Label>Entreprise</Label>
                      <Input value={profile.company} onChange={(e) => setProfile({ ...profile, company: e.target.value })} className="mt-1" />
                    </div>
                  </div>

                  <div>
                    <Label>Adresse</Label>
                    <Input value={profile.address} onChange={(e) => setProfile({ ...profile, address: e.target.value })} placeholder="12 Rue de la Paix" className="mt-1" />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label>Code postal</Label>
                      <Input value={profile.postal_code} onChange={(e) => setProfile({ ...profile, postal_code: e.target.value })} placeholder="75001" className="mt-1" />
                    </div>
                    <div>
                      <Label>Ville</Label>
                      <Input value={profile.city} onChange={(e) => setProfile({ ...profile, city: e.target.value })} placeholder="Paris" className="mt-1" />
                    </div>
                  </div>

                  <div>
                    <Label>Bio</Label>
                    <Textarea value={profile.bio} onChange={(e) => setProfile({ ...profile, bio: e.target.value })} placeholder="Quelques mots sur vous..." className="mt-1" rows={3} />
                  </div>

                  <div className="flex justify-end">
                    <Button onClick={handleSaveProfile} disabled={savingProfile} style={{ backgroundColor: theme.colors?.accent }}>
                      {savingProfile ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                      Enregistrer
                    </Button>
                  </div>
                </CardContent>
              </Card>
              
              {/* Business Info - Only for team members */}
              {isTeamMember && (
                <Card className="card mt-6">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Building2 className="w-5 h-5" />
                      Informations d&apos;entreprise
                    </CardTitle>
                    <CardDescription>Pour la facturation et les contrats</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label>Raison sociale / Nom commercial</Label>
                        <Input 
                          value={profile.company_name} 
                          onChange={(e) => setProfile({ ...profile, company_name: e.target.value, business_name: e.target.value })} 
                          placeholder="SARL Mon Entreprise"
                          className="mt-1" 
                        />
                      </div>
                      <div>
                        <Label>Statut juridique</Label>
                        <Input 
                          value={profile.legal_status} 
                          onChange={(e) => setProfile({ ...profile, legal_status: e.target.value })} 
                          placeholder="Auto-entrepreneur, SARL, SAS..."
                          className="mt-1" 
                        />
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label>SIRET (14 chiffres)</Label>
                        <Input 
                          value={profile.siret} 
                          onChange={(e) => setProfile({ ...profile, siret: e.target.value.replace(/\D/g, '').slice(0, 14) })} 
                          placeholder="12345678901234"
                          maxLength={14}
                          className="mt-1" 
                        />
                      </div>
                      <div>
                        <Label>N° TVA intracommunautaire</Label>
                        <Input 
                          value={profile.tva_number} 
                          onChange={(e) => setProfile({ ...profile, tva_number: e.target.value, vat_number: e.target.value })} 
                          placeholder="FR12345678901"
                          className="mt-1" 
                        />
                      </div>
                    </div>
                    
                    <div>
                      <Label>Adresse de l&apos;entreprise</Label>
                      <Input 
                        value={profile.company_address} 
                        onChange={(e) => setProfile({ ...profile, company_address: e.target.value, business_address: e.target.value })} 
                        placeholder="12 Rue de la Paix"
                        className="mt-1" 
                      />
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label>Code postal</Label>
                        <Input 
                          value={profile.company_postal_code} 
                          onChange={(e) => setProfile({ ...profile, company_postal_code: e.target.value })} 
                          placeholder="75001"
                          className="mt-1" 
                        />
                      </div>
                      <div>
                        <Label>Ville</Label>
                        <Input 
                          value={profile.company_city} 
                          onChange={(e) => setProfile({ ...profile, company_city: e.target.value })} 
                          placeholder="Paris"
                          className="mt-1" 
                        />
                      </div>
                    </div>
                    
                    <div className="flex justify-end">
                      <Button onClick={handleSaveProfile} disabled={savingProfile} style={{ backgroundColor: theme.colors?.accent }}>
                        {savingProfile ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                        Enregistrer
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}
              
              {/* Hourly Rate - Only for team members - Read only (from active contract) */}
              {isTeamMember && (
                <Card className="card mt-6">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Euro className="w-5 h-5" />
                      Taux de facturation
                    </CardTitle>
                    <CardDescription>Défini dans votre contrat actif</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label>Taux horaire (€/h)</Label>
                        <div className="mt-1 p-3 bg-slate-50 rounded-lg border">
                          <span className="text-2xl font-bold" style={{ color: theme.colors?.primary }}>
                            {profile.hourly_rate || 0}€
                          </span>
                          <span className="text-slate-500">/heure</span>
                        </div>
                        <p className="text-xs text-slate-500 mt-1">Tarif défini dans votre contrat actif</p>
                      </div>
                      <div>
                        <Label>Taux journalier (€/jour)</Label>
                        <div className="mt-1 p-3 bg-slate-50 rounded-lg border">
                          <span className="text-2xl font-bold" style={{ color: theme.colors?.primary }}>
                            {profile.daily_rate || (profile.hourly_rate * 8) || 0}€
                          </span>
                          <span className="text-slate-500">/jour</span>
                        </div>
                        <p className="text-xs text-slate-500 mt-1">Base 8h/jour</p>
                      </div>
                    </div>
                    
                    <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                      <p className="text-sm text-blue-700">
                        💡 Pour modifier votre taux horaire, contactez l'agence ou consultez votre contrat dans l'onglet "Mes contrats".
                      </p>
                    </div>
                  </CardContent>
                </Card>
              )}
              
              {/* Bank Details - Only for team members */}
              {isTeamMember && (
                <Card className="card mt-6">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Landmark className="w-5 h-5" />
                      Coordonnées bancaires (RIB)
                    </CardTitle>
                    <CardDescription>Pour recevoir vos paiements</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2">
                      <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5" />
                      <p className="text-sm text-amber-700">
                        Ces informations sont confidentielles et utilisées uniquement pour vos paiements.
                      </p>
                    </div>
                    
                    <div>
                      <Label>Titulaire du compte</Label>
                      <Input 
                        value={profile.account_holder} 
                        onChange={(e) => setProfile({ ...profile, account_holder: e.target.value })} 
                        placeholder="Nom et prénom ou raison sociale"
                        className="mt-1"
                      />
                    </div>
                    
                    <div>
                      <Label>IBAN</Label>
                      <Input 
                        value={profile.iban} 
                        onChange={(e) => setProfile({ ...profile, iban: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '') })} 
                        placeholder="FR76 1234 5678 9012 3456 7890 123"
                        className="mt-1 font-mono"
                      />
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label>BIC / SWIFT</Label>
                        <Input 
                          value={profile.bic} 
                          onChange={(e) => setProfile({ ...profile, bic: e.target.value.toUpperCase() })} 
                          placeholder="BNPAFRPP"
                          className="mt-1 font-mono" 
                        />
                      </div>
                      <div>
                        <Label>Nom de la banque</Label>
                        <Input 
                          value={profile.bank_name} 
                          onChange={(e) => setProfile({ ...profile, bank_name: e.target.value })} 
                          placeholder="BNP Paribas"
                          className="mt-1" 
                        />
                      </div>
                    </div>
                    
                    <div className="flex justify-end">
                      <Button onClick={handleSaveProfile} disabled={savingProfile} style={{ backgroundColor: theme.colors?.accent }}>
                        {savingProfile ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                        Enregistrer
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            {/* Security Tab */}
            <TabsContent value="security">
              <Card className="card">
                <CardHeader>
                  <CardTitle>Changer le mot de passe</CardTitle>
                  <CardDescription>Assurez-vous d&apos;utiliser un mot de passe fort</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label>Mot de passe actuel</Label>
                    <div className="relative mt-1">
                      <Input 
                        type={showPasswords.current ? "text" : "password"} 
                        value={passwords.current} 
                        onChange={(e) => setPasswords({ ...passwords, current: e.target.value })} 
                        className="pr-10"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPasswords({ ...showPasswords, current: !showPasswords.current })}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                      >
                        {showPasswords.current ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                  <div>
                    <Label>Nouveau mot de passe</Label>
                    <div className="relative mt-1">
                      <Input 
                        type={showPasswords.new ? "text" : "password"} 
                        value={passwords.new} 
                        onChange={(e) => setPasswords({ ...passwords, new: e.target.value })} 
                        className="pr-10"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPasswords({ ...showPasswords, new: !showPasswords.new })}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                      >
                        {showPasswords.new ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                  <div>
                    <Label>Confirmer le mot de passe</Label>
                    <div className="relative mt-1">
                      <Input 
                        type={showPasswords.confirm ? "text" : "password"} 
                        value={passwords.confirm} 
                        onChange={(e) => setPasswords({ ...passwords, confirm: e.target.value })} 
                        className="pr-10"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPasswords({ ...showPasswords, confirm: !showPasswords.confirm })}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                      >
                        {showPasswords.confirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                  <div className="flex justify-end">
                    <Button onClick={handleChangePassword} disabled={savingPassword} style={{ backgroundColor: theme.colors?.accent }}>
                      {savingPassword ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Lock className="w-4 h-4 mr-2" />}
                      Changer le mot de passe
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Notifications Tab - Version améliorée */}
            <TabsContent value="notifications">
              {/* Canaux de notification */}
              <Card className="card mb-6">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Bell className="w-5 h-5" />
                    Canaux de notification
                  </CardTitle>
                  <CardDescription>Choisissez comment recevoir vos notifications</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between p-4 rounded-xl border border-[#e2e8f0] hover:bg-slate-50 transition-colors">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                        <Mail className="w-5 h-5 text-blue-600" />
                      </div>
                      <div>
                        <p className="font-medium text-[#1a365d]">Notifications par email</p>
                        <p className="text-sm text-[#64748b]">Recevoir les notifications importantes par email</p>
                      </div>
                    </div>
                    <Switch 
                      checked={notifPrefs.email_enabled} 
                      onCheckedChange={(checked) => updateNotifPref('email_enabled', checked)}
                    />
                  </div>
                  
                  <div className="flex items-center justify-between p-4 rounded-xl border border-[#e2e8f0] hover:bg-slate-50 transition-colors">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                        <Bell className="w-5 h-5 text-purple-600" />
                      </div>
                      <div>
                        <p className="font-medium text-[#1a365d]">Notifications in-app</p>
                        <p className="text-sm text-[#64748b]">Recevoir les notifications dans l&apos;application</p>
                      </div>
                    </div>
                    <Switch 
                      checked={notifPrefs.push_enabled} 
                      onCheckedChange={(checked) => updateNotifPref('push_enabled', checked)}
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Types de notifications par catégorie */}
              {notificationCategories.map((category) => (
                <Card className="card mb-6" key={category.title}>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <category.icon className="w-5 h-5" />
                      {category.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {category.items.map((item) => (
                      <div key={item.key} className="flex items-center justify-between p-3 rounded-lg border border-[#e2e8f0] hover:bg-slate-50 transition-colors">
                        <div>
                          <p className="font-medium text-[#1a365d]">{item.label}</p>
                          <p className="text-sm text-[#64748b]">{item.desc}</p>
                        </div>
                        <Switch 
                          checked={notifPrefs[item.key]} 
                          onCheckedChange={(checked) => updateNotifPref(item.key, checked)}
                        />
                      </div>
                    ))}
                  </CardContent>
                </Card>
              ))}

              {/* Préférences commerciales - Spécifique aux clients */}
              {isClient && (
                <Card className="card mb-6">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <Megaphone className="w-5 h-5" />
                      Communications commerciales
                    </CardTitle>
                    <CardDescription>Gérez vos préférences pour les offres et conseils de l&apos;agence</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Offres commerciales */}
                    <div className="p-4 rounded-xl border border-[#e2e8f0] space-y-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center">
                            <TrendingUp className="w-5 h-5 text-orange-600" />
                          </div>
                          <div>
                            <p className="font-medium text-[#1a365d]">Offres commerciales</p>
                            <p className="text-sm text-[#64748b]">Recevoir nos offres spéciales et promotions</p>
                          </div>
                        </div>
                        <Switch 
                          checked={notifPrefs.commercial_offers} 
                          onCheckedChange={(checked) => updateNotifPref('commercial_offers', checked)}
                        />
                      </div>
                      
                      {notifPrefs.commercial_offers && (
                        <div className="ml-13 pl-4 border-l-2 border-slate-200">
                          <Label className="text-sm text-slate-600 mb-2 block">Canal de réception</Label>
                          <Select 
                            value={notifPrefs.commercial_offers_channel} 
                            onValueChange={(value) => updateNotifPref('commercial_offers_channel', value)}
                          >
                            <SelectTrigger className="w-full">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="email">Par email uniquement</SelectItem>
                              <SelectItem value="manager_only">Via mon manager uniquement</SelectItem>
                              <SelectItem value="both">Les deux (email + manager)</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                    </div>

                    {/* Conseils de l&apos;agence */}
                    <div className="p-4 rounded-xl border border-[#e2e8f0] space-y-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                            <Lightbulb className="w-5 h-5 text-green-600" />
                          </div>
                          <div>
                            <p className="font-medium text-[#1a365d]">Conseils de l&apos;agence</p>
                            <p className="text-sm text-[#64748b]">Recevoir des conseils et astuces de l&apos;agence</p>
                          </div>
                        </div>
                        <Switch 
                          checked={notifPrefs.agency_tips} 
                          onCheckedChange={(checked) => updateNotifPref('agency_tips', checked)}
                        />
                      </div>
                      
                      {notifPrefs.agency_tips && (
                        <div className="ml-13 pl-4 border-l-2 border-slate-200">
                          <Label className="text-sm text-slate-600 mb-2 block">Canal de réception</Label>
                          <Select 
                            value={notifPrefs.agency_tips_channel} 
                            onValueChange={(value) => updateNotifPref('agency_tips_channel', value)}
                          >
                            <SelectTrigger className="w-full">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="email">Par email uniquement</SelectItem>
                              <SelectItem value="manager_only">Via mon manager uniquement</SelectItem>
                              <SelectItem value="both">Les deux (email + manager)</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Bouton de sauvegarde */}
              <div className="flex justify-end">
                <Button 
                  onClick={handleSaveNotifications} 
                  disabled={savingNotifications}
                  className="btn-primary"
                  data-testid="save-notifications-btn"
                >
                  {savingNotifications ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Enregistrement...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Enregistrer les préférences
                    </>
                  )}
                </Button>
              </div>
            </TabsContent>

            {/* Tutoriel Tab */}
            <TabsContent value="tutorial">
              <Card className="border-0 shadow-sm">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Lightbulb className="w-5 h-5" />
                    Visite Guidée
                  </CardTitle>
                  <CardDescription>
                    Relancez la visite guidée pour découvrir les fonctionnalités de votre espace
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <p className="text-sm text-slate-600">
                      La visite guidée vous explique chaque fonctionnalité de votre espace étape par étape, 
                      avec des descriptions détaillées et des vidéos tutorielles.
                    </p>
                    <Button
                      onClick={async () => {
                        try {
                          await api.put('/users/preferences', { 
                            guided_tour_completed: false,
                            guided_tour_skipped: false
                          });
                          toast.success('Visite guidée réinitialisée ! Rechargez la page pour la relancer.');
                          window.location.reload();
                        } catch (error) {
                          toast.error('Erreur lors de la réinitialisation');
                        }
                      }}
                      style={{ backgroundColor: theme.colors?.accent }}
                      className="text-white"
                    >
                      <Lightbulb className="w-4 h-4 mr-2" />
                      Relancer la visite guidée
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
    </MainLayout>
  );
};

export default ProfilePage;
